/**
 * #446 — make the retry thunk obey the SERVER's verdict instead of its own counter.
 *
 * `retrySendMessageThunk` bumped `retryCount` on every MQTT/FCM wake and capped on that client
 * counter, whether or not the server ever granted an attempt. Two measured results (8h, prod):
 *  - ~57 keyed `sent_pending_ack` messages flipped to `failed`/system. The server denied every wake
 *    as `already_terminal` (it had the delivery key), yet five denied wakes still wrote the cap.
 *  - ~180 unkeyed messages capped early: wakes that never reached `acquire-send-slot` (re-pended,
 *    held by the pacer) each counted as a retry.
 *
 * GATE 2 (`has-send-attempt-been-made-to-message`) already returns the server's doc and its grant
 * count; these helpers read them. Pure so they are unit-testable without a store or the network.
 */

const nonEmpty = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

/** Mirrors BE `toStoredWaMessageKey`: a non-empty string, `_serialized`, or `id` + a string chat id. */
export function hasDeliveryKey(key: unknown): boolean {
  if (typeof key === 'string') return key.length > 0;
  if (!key || typeof key !== 'object') return false;
  const k = key as { _serialized?: unknown; id?: unknown; remote?: unknown; remoteJid?: unknown };
  if (nonEmpty(k._serialized)) return true;
  return nonEmpty(k.id) && (nonEmpty(k.remoteJid) || nonEmpty(k.remote));
}

export type ServerDocVerdict =
  | { concluded: false }
  | { concluded: true; reason: 'delivery_key' | 'confirmed' | 'deleted'; status?: string; waMessageKey?: unknown };

/**
 * Whether the server's doc proves the send already concluded. Same predicate as BE
 * `isAlreadyDelivered` (a delivery key, `confirmed`, or `deleted`), the gate that makes the server
 * refuse every further attempt. A `failed` doc without a key stays open so a manual retry still works.
 */
export function serverDocConcluded(doc: unknown): ServerDocVerdict {
  if (!doc || typeof doc !== 'object') return { concluded: false };
  const d = doc as { status?: unknown; waMessageKey?: unknown; deleted?: unknown };
  const status = typeof d.status === 'string' ? d.status : undefined;
  if (hasDeliveryKey(d.waMessageKey)) return { concluded: true, reason: 'delivery_key', status, waMessageKey: d.waMessageKey };
  if (status === 'confirmed') return { concluded: true, reason: 'confirmed', status };
  if (d.deleted === true) return { concluded: true, reason: 'deleted', status };
  return { concluded: false };
}

export interface ServerLockVerdict {
  allowToSend: boolean;
  initialAttemptLockExists: boolean;
  currentAttemptCount: number;
  /** #451 — the server read this count from its lock, so a numeric 0 is real. */
  attemptCountAuthoritative?: boolean;
}

export interface RetryBudgetInput {
  isRetryEnabled: boolean;
  maxRetries: number;
  isManualRetry: boolean;
  clientRetryCount: number;
  /** GATE 2's response; undefined when the check failed (transport error). */
  serverLock: ServerLockVerdict | undefined;
}

export interface RetryBudget {
  /** Where `attemptsUsed` came from. `server` = the Redis grant counter both send gates share. */
  source: 'server' | 'client';
  attemptsUsed: number;
  shouldRetry: boolean;
  exhausted: boolean;
  /** The `retryCount` to write when retrying: one past what was really used, never an inflated count. */
  nextRetryCount: number;
}

export function resolveRetryBudget({ isRetryEnabled, maxRetries, isManualRetry, clientRetryCount, serverLock }: RetryBudgetInput): RetryBudget {
  // GATE 2 probes with attempt 0, so a held lock (deny + lock exists) reports the highest attempt
  // the server granted. A grant or a missing lock carries no usable count. A 0 is usable only when the
  // server marks it authoritative (#451): the window-expired / empty-destination / dedup denies also
  // return lock=true,count=0 unread, and trusting that would reset the budget. Unmarked 0 => client counter.
  const serverCount = serverLock && serverLock.initialAttemptLockExists && !serverLock.allowToSend && Number.isFinite(serverLock.currentAttemptCount)
    && (serverLock.currentAttemptCount > 0 || serverLock.attemptCountAuthoritative === true)
    ? serverLock.currentAttemptCount
    : undefined;
  const source = serverCount === undefined ? 'client' : 'server';
  const attemptsUsed = serverCount ?? clientRetryCount;
  const exhausted = isRetryEnabled && attemptsUsed >= maxRetries;
  const shouldRetry = isRetryEnabled && (!exhausted || isManualRetry);
  return { source, attemptsUsed, shouldRetry, exhausted, nextRetryCount: attemptsUsed + 1 };
}
